import { makeAutoObservable, runInAction, toJS } from "mobx";
import { carbonInventoryService } from "../services";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { promiseWithMinDelay } from "@/lib/utils/promiseWithMinDelay";

const toPositiveInt = (value, fallback) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return parsed;
};

class CarbonSurveysStore {
  isUploading = false;
  hasDataChanges = false;
  carbonSurveysList = [];
  carbonSurveysData = {
    data: {
      scope1: {
        mode: "total",
        activities: {
          stationary: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
          mobile: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
          fugitive: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
          process: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
        },
        totalEvidenceFiles: [],
        totalco2eUnit: "tCO2e",
      },
      scope2: {
        mode: "total",
        activities: {
          electricity: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
          steam: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
          cooling: {
            period: "annually",
            entries: [{ evidenceFiles: [], unit: "tCO2e" }],
          },
        },
        totalEvidenceFiles: [],
        totalco2eUnit: "tCO2e",
      },
    },
    evidenceFiles: [],
  };
  page = 1;
  pageSize = 10;
  total = 0;
  totalPages = 0;
  tempFiles = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: false });
  }

  setPage = (page) => {
    this.page = toPositiveInt(page, 1);
  };

  setPageSize = (size) => {
    this.pageSize = toPositiveInt(size, 10);
    this.page = 1;
  };

  handleChange = (value, key1, key2, { silent = false, index = 0 } = {}) => {
    runInAction(() => {
      if (!silent) {
        this.hasDataChanges = true;
      }
      switch (key1) {
        case "stationary":
        case "mobile":
        case "fugitive":
        case "process":
          if (key2 === "period") {
            this.carbonSurveysData["data"]["scope1"]["activities"][key1][key2] =
              value;
          } else if (
            this.carbonSurveysData["data"]["scope1"]["mode"] === "activities"
          ) {
            this.carbonSurveysData["data"]["scope1"]["activities"][key1][
              "entries"
            ][index][key2] = value;
            this.carbonSurveysData["data"]["scope1"]["activities"][key1][
              "entries"
            ][index]["index"] = index;
          }
          break;
        case "electricity":
        case "steam":
        case "cooling":
          if (key2 === "period") {
            this.carbonSurveysData["data"]["scope2"]["activities"][key1][key2] =
              value;
          } else if (
            this.carbonSurveysData["data"]["scope2"]["mode"] === "activities"
          ) {
            this.carbonSurveysData["data"]["scope2"]["activities"][key1][
              "entries"
            ][index][key2] = value;
            this.carbonSurveysData["data"]["scope2"]["activities"][key1][
              "entries"
            ][index]["index"] = index;
          }
          break;

        default:
          if (key2 === undefined) {
            this.carbonSurveysData[key1] = value;
            return;
          }
          this.carbonSurveysData["data"][key1][key2] = value;
          break;
      }
    });
  };

  handleClear = async () => {
    this.carbonSurveysData = {
      data: {
        scope1: {
          mode: "total",
          activities: {
            stationary: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
            mobile: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
            fugitive: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
            process: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
          },
          totalEvidenceFiles: [],
          totalco2eUnit: "tCO2e",
        },
        scope2: {
          mode: "total",
          activities: {
            electricity: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
            steam: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
            cooling: {
              period: "annually",
              entries: [{ evidenceFiles: [], unit: "tCO2e" }],
            },
          },
          totalEvidenceFiles: [],
          totalco2eUnit: "tCO2e",
        },
      },
      evidenceFiles: [],
    };
    if (this.tempFiles.length > 0) {
      try {
        const result = await carbonInventoryService.deleteFiles({
          fileIds: this.tempFiles,
        });
      } catch (error) {}
    }
    this.tempFiles = [];
  };

  // get carbon Inventory
  getCarbonInventorySurvey = async () => {
    runInAction(() => {});
    try {
      const params = {
        page: this.page,
        limit: this.pageSize,
      };
      const data = await carbonInventoryService.getCarbonSurvey(params);
      const pagination = data.pagination || {};
      runInAction(() => {
        this.carbonSurveysList = data.submissions;
        this.total = Number(pagination.total) || 0;
        this.totalPages = Number(pagination.totalPages) || 0;
      });
    } catch (error) {
      showToast.error(error?.message || "Failed to Load!");
      runInAction(() => {
        this.carbonSurveysList = [];
        this.total = 0;
        this.totalPages = 0;
      });
    } finally {
      runInAction(() => {});
    }
  };

  // get carbon Inventory data by id
  getCarbonSurveysDataById = async (surveyId) => {
    runInAction(() => {});
    try {
      const data = await carbonInventoryService.getCarbonSurveyById(surveyId);
      runInAction(() => {
        this.carbonSurveysData = data.result;
        this.hasDataChanges = false;
      });
    } catch (error) {
      showToast.error(error?.message || "Failed to load!");
    } finally {
      runInAction(() => {});
    }
  };

  // save
  handleSaveCarbonInventory = async () => {
    try {
      const data = await carbonInventoryService.createCarbonSurvey(
        this.carbonSurveysData,
      );
      if (!data) {
        throw new Error("Failed to save!");
      }
      runInAction(() => {
        const result = data.submission;
        Object.assign(this.carbonSurveysData, result);
        showToast.success("Successfully saved!");
        this.hasDataChanges = false;
        this.handleClear();
      });
      return true;
    } catch (error) {
      showToast.error(error?.message || "Failed to save!");
      return false;
    } finally {
      runInAction(() => {});
    }
  };

  // update
  handleUpdateCarbonInventory = async () => {
    runInAction(() => {});
    try {
      const scope1 = this.carbonSurveysData.scope1;
      const scope2 = this.carbonSurveysData.scope2;
      const companyName = this.carbonSurveysData.companyName;
      const reportingYear = this.carbonSurveysData.reportingYear;
      const payload = {
        data: {
          scope1,
          scope2,
          generalInformation: {
            companyName,
            reportingYear,
          },
        },
        reportingYear: reportingYear,
        companyName: companyName,
        // evidenceFiles,
      };
      const data = await carbonInventoryService.updateCarbonSurvey(
        this.carbonSurveysData.id,
        payload,
      );
      if (!data) {
        throw new Error("Failed to update!");
      }
      runInAction(() => {
        const result = data.submission;
        showToast.success("Successfully updated!");
        this.carbonSurveysData = result;
        this.hasDataChanges = false;
      });
      return true;
    } catch (error) {
      showToast.error(error?.message || "Failed to update!");
      return false;
    } finally {
      runInAction(() => {});
    }
  };

  // delete
  handleDeleteCarbonInventory = async (surveyId) => {
    runInAction(() => {});
    try {
      const data = await carbonInventoryService.deleteCarbonSurvey(surveyId);

      if (data?.result?.deletedCount !== 1) {
        throw new Error("Failed to delete!");
      }
      showToast.success("Successfully deleted!");
      await this.getCarbonInventorySurvey();
      return true;
    } catch (error) {
      showToast.error(error?.message || "Failed to delete!");
    } finally {
      runInAction(() => {});
    }
  };

  // Evidence File
  uploadFile = async (file, scope, mode) => {
    runInAction(() => {
      this.isUploading = true;
    });
    const { user } = useAuthStore();

    const companyId = user?.companyId;
    if (!companyId) return;
    const formData = new FormData();
    formData.append("files", file);

    try {
      const uploadPromise = carbonInventoryService.uploadFile(
        companyId,
        formData,
      );
      showToast.promise(uploadPromise, {
        loading: "Uploading",
        success: "Successfully uploaded",
        error: "Failed to upload",
      });
      const result = await promiseWithMinDelay(uploadPromise);
      this.carbonSurveysData.evidenceFiles.push(result.file.fileId);
      this.tempFiles.push(result.file.fileId);
      if (mode === "total") {
        this.carbonSurveysData["data"][scope]["totalEvidenceFiles"].push(
          result.file,
        );
      } else if (mode === "activities") {
        switch (scope) {
          case "stationary":
          case "mobile":
          case "fugitive":
          case "process":
            this.carbonSurveysData["data"]["scope1"]["activities"][scope][
              "entries"
            ][0]["evidenceFiles"].push(result.file);
            break;
          case "electricity":
          case "steam":
          case "cooling":
            this.carbonSurveysData["data"]["scope2"]["activities"][scope][
              "entries"
            ][0]["evidenceFiles"].push(result.file);
            break;
          default:
            break;
        }
      }
      this.hasDataChanges = true;
    } finally {
      runInAction(() => {
        this.isUploading = false;
      });
    }
  };

  downloadFile = async (proof) => {
    const fileId = proof.fileId;
    if (!fileId) return;
    const downloadPromise = carbonInventoryService.downloadFile(proof);
    showToast.promise(downloadPromise, {
      loading: "Downloading",
      success: "Successfully downloaded",
      error: "Failed to download",
    });

    await promiseWithMinDelay(downloadPromise);
  };

  removeFile = async (fileId, mode, scope, isEdit) => {
    if (!isEdit) {
      try {
        const result = await carbonInventoryService.deleteFiles({
          fileIds: [fileId],
        });
        if (!result) {
          throw new Error("Failed to delete!");
        }
        showToast.success("Successfully deleted!");
      } catch (error) {
        return showToast.error(error?.message || "Failed to delete!");
      }
    }
    this.carbonSurveysData.evidenceFiles =
      this.carbonSurveysData.evidenceFiles?.filter((v) => v !== fileId);

    if (mode === "total") {
      this.carbonSurveysData.data[scope].totalEvidenceFiles =
        this.carbonSurveysData.data[scope].totalEvidenceFiles?.filter(
          (v) => v.fileId !== fileId,
        );
    } else if (mode === "activities") {
      switch (scope) {
        case "stationary":
        case "mobile":
        case "fugitive":
        case "process":
          this.carbonSurveysData["data"]["scope1"]["activities"][scope][
            "entries"
          ][0]["evidenceFiles"] = this.carbonSurveysData["data"]["scope1"][
            "activities"
          ][scope]["entries"][0]["evidenceFiles"].filter(
            (v) => v._id !== fileId,
          );
          break;
        case "electricity":
        case "steam":
        case "cooling":
          this.carbonSurveysData["data"]["scope2"]["activities"][scope][
            "entries"
          ][0]["evidenceFiles"] = this.carbonSurveysData["data"]["scope2"][
            "activities"
          ][scope]["entries"][0]["evidenceFiles"].filter(
            (v) => v._id !== fileId,
          );
          break;
        default:
          break;
      }
    }
    this.hasDataChanges = true;
  };
}

export const carbonSurveysStore = new CarbonSurveysStore();
